import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import DateFilterPicker from "./DateFilterPicker";
import { useSearchParams } from "react-router-dom";
import FilterLabel from "./FilterLabel";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronRight } from 'lucide-react';
import { useSearchStore } from "@/stores/SearchStore";

function calcDate(days) {
  const daysInMilliseconds = days * 24 * 60 * 60 * 1000;
  const date = Math.floor((Date.now() + daysInMilliseconds) / 1000);
  return date;
}

export default function DateFilter({
  title,
  urlName,
  filters,
  defaultOpen = true,
  defaultFilter = null, // Add this new prop
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [initialRender, setInitialRender] = useState(true);

  // Apply default filter on initial render if no filter is already selected
  useEffect(() => {
    if (initialRender && defaultFilter && !searchParams.get(urlName)) {
      const filter = filters.find(f => f.label === defaultFilter);
      if (filter) {
        searchParams.set(urlName, calcDate(filter.days));
        setSelectedFilter(filter.label);
        setAllSearchParams();
      }
      setInitialRender(false);
    }
  }, [initialRender, defaultFilter, filters, urlName, searchParams]);

  // Reset selectedFilter when urlName is removed from searchParams
  useEffect(() => {
    // Get the deadline from the URL
    const filterParam = searchParams.get(urlName);

    if (!filterParam) {
      setSelectedFilter(null);
      return;
    }

    const deadlineValue = parseInt(filterParam);

    filters.forEach((filter) => {
      const margin = 12 * 60 * 60; // 12 hours in seconds
      if (Math.abs(calcDate(filter.days) - deadlineValue) <= margin) {
        setSelectedFilter(filter.label);
      }
    });
  }, [searchParams, urlName, filters]);

  function setAllSearchParams() {
    setSearchParams(searchParams);
    useSearchStore.setState({ searchPageSearchParams: searchParams });
  }

  function handleFilterClick(filter) {
    const { days } = filter;
    const isChecked = selectedFilter === filter.label;

    if (!isChecked) {
      try {
        searchParams.set(urlName, calcDate(days));
      } catch (e) {}
    } else {
      searchParams.delete(urlName);
    }

    setSelectedFilter(isChecked ? null : filter.label);
    setAllSearchParams();
  }

  return (
    <div className="space-y-2">
      <Collapsible defaultOpen={defaultOpen} className="group/collapsible">
        <CollapsibleTrigger asChild>
          <Button
            variant="link"
            className="w-full justify-between text-background-foreground"
          >
            <FilterLabel>{title}</FilterLabel>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-wrap gap-2">
          {filters.map((filter) =>
            ["future", "past"].includes(filter.days) ? (
              <DateFilterPicker
                key={filter.label}
                urlName={urlName}
                future={filter.days === "future"}
                className="bg-secondary text-secondary-foreground"
                setSelectedFilter={setSelectedFilter}
                selectedFilter={selectedFilter}
              />
            ) : (
              <Button
                key={filter.label}
                variant={
                  selectedFilter === filter.label ? "default" : "secondary"
                }
                size="sm"
                onClick={() => handleFilterClick(filter)}
              >
                {filter.icon && <filter.icon className="mr-1 h-4 w-4" />}
                {filter.label}
              </Button>
            )
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}